/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box } from '@mui/material';
import { createContext, useEffect, useState } from 'react';

type User = {
  loggedIn: boolean;
  username?: string;
  userId?: string;
  orgId?: string; // Added orgId
};

export type GlobalStateContextType = {
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User>>;
};

export const GlobalStateContext = createContext<GlobalStateContextType>({
  user: { loggedIn: false },
  setUser: () => {},
});

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider component for handling user authentication and providing global state context.
 *
 * @param {AuthProviderProps} children - The child components to be rendered within the provider
 * @return {JSX.Element} The provider component with global state context
 */
const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const storedUser = localStorage.getItem('user');
  const authentication = localStorage.getItem('token');

  let parsedStoredUser = null;
  try {
    parsedStoredUser = storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.error('Failed to parse stored user:', error);
    localStorage.removeItem('user');
  }

  const initialUserState: User = {
    loggedIn: !!authentication, // true if authentication token exists
    username: parsedStoredUser?.username || '',
    userId: parsedStoredUser?.id || '',
    orgId: parsedStoredUser?.userOrganizations?.[0]?.organizationId || '', // Added orgId with safe navigation
  };

  const [user, setUser] = useState<User>(initialUserState);

  useEffect(() => {
    if (authentication && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser({
          loggedIn: true,
          username: parsedUser.username || '',
          userId: parsedUser.id || '',
          orgId: parsedUser.userOrganizations?.[0]?.organizationId || '', // Added orgId with safe navigation
        });
      } catch (error) {
        console.error('Failed to parse user in useEffect:', error);
        setUser({
          loggedIn: false,
          username: '',
          userId: '',
          orgId: '',
        });
      }
    } else {
      setUser({
        loggedIn: false,
        username: '',
        userId: '',
        orgId: '', // Added orgId
      });
    }
  }, [storedUser, authentication]);

  // Return loading state while user is being authenticated
  if (authentication && !user.loggedIn) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100vh',
        }}
      >
        Loading ...
      </Box>
    );
  }

  return (
    <GlobalStateContext.Provider value={{ user, setUser }}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export default AuthProvider;
